import axios from 'axios';
import { getAuthHeaders } from './auth';

const API_BASE = '/api';

// ============================================================================
// Type Definitions
// ============================================================================

export interface Faculty {
  id: number;
  faculty_code: number;
  name_ar: string;
  name_en: string;
  program_count: number;
  created_at: string;
}

export interface Domain {
  id: number;
  slug: string;
  name_ar: string;
  name_en: string;
  system_prompt_override_ar?: string;
  system_prompt_override_en?: string;
  widget_title_ar?: string;
  widget_title_en?: string;
  widget_subtitle_ar?: string;
  widget_subtitle_en?: string;
  primary_color?: string;
  faculty_code?: number;
  is_active: boolean;
  hero_title_ar?: string;
  hero_title_en?: string;
  hero_subtitle_ar?: string;
  hero_subtitle_en?: string;
  suggested_questions_ar?: string[];
  suggested_questions_en?: string[];
  logo_url?: string;
  hosted_page_enabled?: boolean;
  handoff_contact?: HandoffContact | null;
  document_count: number;
  created_at: string;
}

export interface HandoffContact {
  unit_ar?: string | null;
  unit_en?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface DomainCreate {
  slug: string;
  name_ar: string;
  name_en: string;
  system_prompt_override_ar?: string;
  system_prompt_override_en?: string;
  widget_title_ar?: string;
  widget_title_en?: string;
  widget_subtitle_ar?: string;
  widget_subtitle_en?: string;
  primary_color?: string;
  faculty_code?: number;
  is_active?: boolean;
  hero_title_ar?: string;
  hero_title_en?: string;
  hero_subtitle_ar?: string;
  hero_subtitle_en?: string;
  suggested_questions_ar?: string[];
  suggested_questions_en?: string[];
  logo_url?: string;
  hosted_page_enabled?: boolean;
  handoff_contact?: HandoffContact | null;
}

export interface DomainUpdate {
  slug?: string;
  name_ar?: string;
  name_en?: string;
  system_prompt_override_ar?: string;
  system_prompt_override_en?: string;
  widget_title_ar?: string;
  widget_title_en?: string;
  widget_subtitle_ar?: string;
  widget_subtitle_en?: string;
  primary_color?: string;
  faculty_code?: number;
  is_active?: boolean;
  hero_title_ar?: string;
  hero_title_en?: string;
  hero_subtitle_ar?: string;
  hero_subtitle_en?: string;
  suggested_questions_ar?: string[];
  suggested_questions_en?: string[];
  logo_url?: string;
  hosted_page_enabled?: boolean;
  handoff_contact?: HandoffContact | null;
}

export interface Program {
  id: number;
  name_ar: string;
  name_en: string;
  faculty?: string;
  faculty_code?: number;
  program_code?: number;
  aliases: string[];
  created_at: string;
}

export interface ProgramCreate {
  name_ar: string;
  name_en: string;
  faculty?: string;
  aliases?: string[];
}

export interface ProgramUpdate {
  name_ar?: string;
  name_en?: string;
  faculty?: string;
  aliases?: string[];
}

export interface Contact {
  id: number;
  unit: string;
  name_ar?: string;
  name_en?: string;
  phone?: string;
  email?: string;
  hours?: string;
  location?: string;
  created_at: string;
}

export interface ContactCreate {
  unit: string;
  name_ar?: string;
  name_en?: string;
  phone?: string;
  email?: string;
  hours?: string;
  location?: string;
}

export interface ContactUpdate {
  unit?: string;
  name_ar?: string;
  name_en?: string;
  phone?: string;
  email?: string;
  hours?: string;
  location?: string;
}

export interface Department {
  id: number;
  name_ar: string;
  name_en: string;
  head_name?: string;
  contact_email?: string;
  contact_phone?: string;
  created_at: string;
}

export interface DepartmentCreate {
  name_ar: string;
  name_en: string;
  head_name?: string;
  contact_email?: string;
  contact_phone?: string;
}

export interface DepartmentUpdate {
  name_ar?: string;
  name_en?: string;
  head_name?: string;
  contact_email?: string;
  contact_phone?: string;
}

export interface AdmissionGrade {
  id: number;
  program_id: number;
  program_name_ar?: string;
  program_name_en?: string;
  year: number;
  grade: number;
  admission_type?: string;
  track?: string;
  created_at: string;
}

export interface AdmissionGradeCreate {
  program_id: number;
  year: number;
  grade: number;
  admission_type?: string;
  track?: string;
}

export interface AdmissionGradeUpdate {
  program_id?: number;
  year?: number;
  grade?: number;
  admission_type?: string;
  track?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
  pages: number;
}

export interface BulkOperationResponse {
  success: boolean;
  created: number;
  updated: number;
  deleted: number;
  errors: string[];
}

// ============================================================================
// Faculties API
// ============================================================================

export const getFaculties = async (
  page: number = 1, pageSize: number = 50, search?: string
): Promise<PaginatedResponse<Faculty>> => {
  const params: any = { page, page_size: pageSize };
  if (search) params.search = search;
  const response = await axios.get(`${API_BASE}/admin/data/faculties`, { headers: getAuthHeaders(), params });
  return { ...response.data, pages: Math.ceil(response.data.total / pageSize) };
};

export const deleteFaculty = async (id: number): Promise<void> => {
  await axios.delete(`${API_BASE}/admin/data/faculties/${id}`, { headers: getAuthHeaders() });
};

export const bulkDeleteFaculties = async (ids: number[]): Promise<BulkOperationResponse> => {
  const response = await axios.post(`${API_BASE}/admin/data/faculties/bulk-delete`, { ids }, { headers: getAuthHeaders() });
  return response.data;
};

export const bulkUploadFaculties = async (file: File): Promise<BulkOperationResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API_BASE}/admin/data/faculties/bulk-upload`, formData, {
    headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

// ============================================================================
// Domains API
// ============================================================================

export const getDomains = async (
  page: number = 1, pageSize: number = 50, search?: string
): Promise<PaginatedResponse<Domain>> => {
  const params: any = { page, page_size: pageSize };
  if (search) params.search = search;
  const response = await axios.get(`${API_BASE}/admin/data/domains`, { headers: getAuthHeaders(), params });
  return { ...response.data, pages: Math.ceil(response.data.total / pageSize) };
};

export const getDomain = async (id: number): Promise<Domain> => {
  const response = await axios.get(`${API_BASE}/admin/data/domains/${id}`, { headers: getAuthHeaders() });
  return response.data;
};

export const createDomain = async (data: DomainCreate): Promise<Domain> => {
  const response = await axios.post(`${API_BASE}/admin/data/domains`, data, { headers: getAuthHeaders() });
  return response.data;
};

export const updateDomain = async (id: number, data: DomainUpdate): Promise<Domain> => {
  const response = await axios.put(`${API_BASE}/admin/data/domains/${id}`, data, { headers: getAuthHeaders() });
  return response.data;
};

export const deleteDomain = async (id: number): Promise<void> => {
  await axios.delete(`${API_BASE}/admin/data/domains/${id}`, { headers: getAuthHeaders() });
};

export const bulkDeleteDomains = async (ids: number[]): Promise<BulkOperationResponse> => {
  const response = await axios.post(`${API_BASE}/admin/data/domains/bulk-delete`, { ids }, { headers: getAuthHeaders() });
  return response.data;
};

// ============================================================================
// Programs API
// ============================================================================

export const getPrograms = async (
  page: number = 1,
  pageSize: number = 50,
  search?: string
): Promise<PaginatedResponse<Program>> => {
  const params: any = { page, page_size: pageSize };
  if (search) params.search = search;
  
  const response = await axios.get(`${API_BASE}/admin/data/programs`, {
    headers: getAuthHeaders(),
    params
  });
  return response.data;
};

export const getProgram = async (id: number): Promise<Program> => {
  const response = await axios.get(`${API_BASE}/admin/data/programs/${id}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createProgram = async (data: ProgramCreate): Promise<Program> => {
  const response = await axios.post(`${API_BASE}/admin/data/programs`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateProgram = async (id: number, data: ProgramUpdate): Promise<Program> => {
  const response = await axios.put(`${API_BASE}/admin/data/programs/${id}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteProgram = async (id: number): Promise<void> => {
  await axios.delete(`${API_BASE}/admin/data/programs/${id}`, {
    headers: getAuthHeaders()
  });
};

export const bulkDeletePrograms = async (ids: number[]): Promise<BulkOperationResponse> => {
  const response = await axios.post(
    `${API_BASE}/admin/data/programs/bulk-delete`,
    { ids },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const bulkUploadPrograms = async (file: File): Promise<BulkOperationResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(
    `${API_BASE}/admin/data/programs/bulk-upload`,
    formData,
    {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

// ============================================================================
// Contacts API
// ============================================================================

export const getContacts = async (
  page: number = 1,
  pageSize: number = 50,
  search?: string
): Promise<PaginatedResponse<Contact>> => {
  const params: any = { page, page_size: pageSize };
  if (search) params.search = search;
  
  const response = await axios.get(`${API_BASE}/admin/data/contacts`, {
    headers: getAuthHeaders(),
    params
  });
  return response.data;
};

export const getContact = async (id: number): Promise<Contact> => {
  const response = await axios.get(`${API_BASE}/admin/data/contacts/${id}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createContact = async (data: ContactCreate): Promise<Contact> => {
  const response = await axios.post(`${API_BASE}/admin/data/contacts`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateContact = async (id: number, data: ContactUpdate): Promise<Contact> => {
  const response = await axios.put(`${API_BASE}/admin/data/contacts/${id}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteContact = async (id: number): Promise<void> => {
  await axios.delete(`${API_BASE}/admin/data/contacts/${id}`, {
    headers: getAuthHeaders()
  });
};

export const bulkDeleteContacts = async (ids: number[]): Promise<BulkOperationResponse> => {
  const response = await axios.post(
    `${API_BASE}/admin/data/contacts/bulk-delete`,
    { ids },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const bulkUploadContacts = async (file: File): Promise<BulkOperationResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(
    `${API_BASE}/admin/data/contacts/bulk-upload`,
    formData,
    {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

// ============================================================================
// Departments API
// ============================================================================

export const getDepartments = async (
  page: number = 1,
  pageSize: number = 50,
  search?: string
): Promise<PaginatedResponse<Department>> => {
  const params: any = { page, page_size: pageSize };
  if (search) params.search = search;
  
  const response = await axios.get(`${API_BASE}/admin/data/departments`, {
    headers: getAuthHeaders(),
    params
  });
  return response.data;
};

export const getDepartment = async (id: number): Promise<Department> => {
  const response = await axios.get(`${API_BASE}/admin/data/departments/${id}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createDepartment = async (data: DepartmentCreate): Promise<Department> => {
  const response = await axios.post(`${API_BASE}/admin/data/departments`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateDepartment = async (id: number, data: DepartmentUpdate): Promise<Department> => {
  const response = await axios.put(`${API_BASE}/admin/data/departments/${id}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteDepartment = async (id: number): Promise<void> => {
  await axios.delete(`${API_BASE}/admin/data/departments/${id}`, {
    headers: getAuthHeaders()
  });
};

export const bulkDeleteDepartments = async (ids: number[]): Promise<BulkOperationResponse> => {
  const response = await axios.post(
    `${API_BASE}/admin/data/departments/bulk-delete`,
    { ids },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const bulkUploadDepartments = async (file: File): Promise<BulkOperationResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(
    `${API_BASE}/admin/data/departments/bulk-upload`,
    formData,
    {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};

// ============================================================================
// Admission Grades API
// ============================================================================

export const getAdmissionGrades = async (
  page: number = 1,
  pageSize: number = 50,
  search?: string,
  year?: number
): Promise<PaginatedResponse<AdmissionGrade>> => {
  const params: any = { page, page_size: pageSize };
  if (search) params.search = search;
  if (year) params.year = year;
  
  const response = await axios.get(`${API_BASE}/admin/data/admission-grades`, {
    headers: getAuthHeaders(),
    params
  });
  return response.data;
};

export const getAdmissionGrade = async (id: number): Promise<AdmissionGrade> => {
  const response = await axios.get(`${API_BASE}/admin/data/admission-grades/${id}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createAdmissionGrade = async (data: AdmissionGradeCreate): Promise<AdmissionGrade> => {
  const response = await axios.post(`${API_BASE}/admin/data/admission-grades`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateAdmissionGrade = async (id: number, data: AdmissionGradeUpdate): Promise<AdmissionGrade> => {
  const response = await axios.put(`${API_BASE}/admin/data/admission-grades/${id}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteAdmissionGrade = async (id: number): Promise<void> => {
  await axios.delete(`${API_BASE}/admin/data/admission-grades/${id}`, {
    headers: getAuthHeaders()
  });
};

export const bulkDeleteAdmissionGrades = async (ids: number[]): Promise<BulkOperationResponse> => {
  const response = await axios.post(
    `${API_BASE}/admin/data/admission-grades/bulk-delete`,
    { ids },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const bulkUploadAdmissionGrades = async (file: File): Promise<BulkOperationResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await axios.post(
    `${API_BASE}/admin/data/admission-grades/bulk-upload`,
    formData,
    {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data'
      }
    }
  );
  return response.data;
};
